import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Search,
  Download,
  ChevronRight,
  Users,
  Bot,
  Calendar,
  Mail,
} from 'lucide-react';
import RefreshButton from '../../components/layout/RefreshButton';
import { adminApi } from '../../services/adminApi';
import { exportToCSV, exportToJSON, prepareUsersForExport } from '../../utils/export';

export default function UserList() {
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);

  const fetchUsers = useCallback(async (searchQuery = '') => {
    try {
      const response = await adminApi.getUsers({
        page,
        limit: 20,
        search: searchQuery || undefined,
      });
      setUsers(response.users || []);
      setTotalPages(response.totalPages || 1);
      setTotalUsers(response.total || 0);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  }, [page]);

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      await fetchUsers(appliedSearch);
      setIsLoading(false);
    };
    loadData();
  }, [fetchUsers, appliedSearch]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchUsers(appliedSearch);
    setIsRefreshing(false);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setAppliedSearch(searchInput.trim());
  };

  // Clear search results when input is emptied
  useEffect(() => {
    if (searchInput === '' && appliedSearch !== '') {
      setPage(1);
      setAppliedSearch('');
    }
  }, [searchInput, appliedSearch]);

  const handleExport = async (format) => {
    try {
      // Fetch all users for export
      const response = await adminApi.getUsers({ limit: 10000 });
      const exportData = prepareUsersForExport(response.users || []);

      if (format === 'csv') {
        exportToCSV(exportData, 'users-export', [
          { key: 'name', label: 'Name' },
          { key: 'email', label: 'Email' },
          { key: 'status', label: 'Status' },
          { key: 'agentCount', label: 'Agents' },
          { key: 'joinedDate', label: 'Joined Date' },
          { key: 'lastActive', label: 'Last Active' },
        ]);
      } else {
        exportToJSON(exportData, 'users-export');
      }
    } catch (err) {
      console.error('Export failed:', err);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">Users</h1>
          <p className="text-text-secondary mt-1">
            Manage all platform users ({totalUsers} total)
          </p>
        </div>
        <div className="flex items-center gap-3">
          <RefreshButton
            onClick={handleRefresh}
            isLoading={isRefreshing}
            lastUpdated={lastUpdated}
          />
          <div className="relative group">
            <button className="btn-secondary">
              <Download className="w-4 h-4" />
              Export
            </button>
            <div className="absolute right-0 mt-2 w-32 bg-white rounded-lg shadow-lg border border-border py-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
              <button
                onClick={() => handleExport('csv')}
                className="w-full px-4 py-2 text-left text-sm hover:bg-bg-secondary"
              >
                Export CSV
              </button>
              <button
                onClick={() => handleExport('json')}
                className="w-full px-4 py-2 text-left text-sm hover:bg-bg-secondary"
              >
                Export JSON
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="card p-4 mb-6">
        <form onSubmit={handleSearch} className="flex gap-4">
          <div className="flex-1 relative">
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="input pr-10 w-full"
            />
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted pointer-events-none" />
          </div>
          <button type="submit" className="btn-primary whitespace-nowrap">
            Search
          </button>
        </form>
      </div>

      {/* Users Table */}
      <div className="card">
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>User</th>
                <th>Email</th>
                <th>Agents</th>
                <th>Joined</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i}>
                    <td colSpan={6}>
                      <div className="h-12 bg-bg-secondary rounded animate-pulse"></div>
                    </td>
                  </tr>
                ))
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-12 text-text-muted">
                    No users found
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user.id}>
                    <td>
                      <div className="flex items-center gap-3">
                        {user.picture ? (
                          <img
                            src={user.picture}
                            alt={user.name}
                            className="w-10 h-10 rounded-full"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                            <span className="text-sm font-medium text-primary">
                              {user.name?.charAt(0) || 'U'}
                            </span>
                          </div>
                        )}
                        <div>
                          <p className="font-medium text-text-primary">
                            {user.name || 'Unknown'}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="flex items-center gap-2 text-text-secondary">
                        <Mail className="w-4 h-4" />
                        {user.email}
                      </div>
                    </td>
                    <td>
                      <div className="flex items-center gap-2">
                        <Bot className="w-4 h-4 text-text-muted" />
                        <span>{user.agentCount || 0}</span>
                      </div>
                    </td>
                    <td>
                      <div className="flex items-center gap-2 text-text-secondary">
                        <Calendar className="w-4 h-4" />
                        {formatDate(user.createdAt)}
                      </div>
                    </td>
                    <td>
                      <span
                        className={`badge ${
                          user.isActive !== false
                            ? 'badge-success'
                            : 'badge-danger'
                        }`}
                      >
                        {user.isActive !== false ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <Link
                        to={`/users/${user.id}`}
                        className="p-2 rounded-lg hover:bg-bg-secondary text-text-muted hover:text-primary transition-colors inline-flex"
                      >
                        <ChevronRight className="w-5 h-5" />
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="p-4 border-t border-border flex items-center justify-between">
            <p className="text-sm text-text-muted">
              Page {page} of {totalPages}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="btn-ghost"
              >
                Previous
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="btn-ghost"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
